/**
 * Raycasting Utilities
 * Mesh detection untuk click & hover interactions
 */

/**
 * Helper function untuk check if material name is allowed untuk click
 * Supports base names dan variants dengan numeric suffixes (e.g., "Edit Here.001")
 */
export function isAllowedMaterialName(materialName) {
  const allowedMaterialNames = [
    'Edit Here',
    'Screen',
    'Body Color',
    'Background',
  ]

  // Check exact match first
  if (allowedMaterialNames.includes(materialName)) {
    return true
  }

  // Check if material name starts with allowed name followed by .XXX (numeric suffix)
  return allowedMaterialNames.some((baseName) => {
    if (materialName.startsWith(baseName + '.')) {
      // Extract suffix after the dot
      const suffix = materialName.substring(baseName.length + 1)
      // Check if suffix is numeric (e.g., "001", "002", "1", "2", etc.)
      return /^\d+$/.test(suffix)
    }
    return false
  })
}

/**
 * Get active camera (default atau GLB camera)
 */
export function getActiveCamera(
  cameraRef,
  selectedCameraRef,
  modelRef
) {
  let activeCamera = cameraRef.current

  // If a GLB camera is selected, use it instead of default camera
  if (
    selectedCameraRef.current !== 'default' &&
    modelRef.current?.userData.cameras
  ) {
    const cameraData = modelRef.current.userData.cameras.find(
      (c) => c.name === selectedCameraRef.current
    )
    if (cameraData && cameraData.camera) {
      activeCamera = cameraData.camera
      // Ensure camera matrix is updated (important for animated cameras)
      activeCamera.updateMatrixWorld(true)
    }
  }

  return activeCamera
}

/**
 * Detect mesh under cursor dengan raycasting
 * Hanya detect meshes dengan allowed material names
 */
export function detectMeshUnderCursor(
  event,
  containerRef,
  cameraRef,
  sceneRef,
  raycasterRef,
  mouseRef,
  modelRef,
  selectedCameraRef
) {
  if (
    !containerRef.current ||
    !cameraRef.current ||
    !sceneRef.current ||
    !raycasterRef.current ||
    !mouseRef.current ||
    !modelRef.current
  ) {
    return null
  }

  const rect = containerRef.current.getBoundingClientRect()
  mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

  // Use active camera instead of default camera for accurate raycasting
  const activeCamera = getActiveCamera(cameraRef, selectedCameraRef, modelRef)
  raycasterRef.current.setFromCamera(mouseRef.current, activeCamera)

  // Get all meshes from the model, filtered by material name
  const meshes = []
  modelRef.current.traverse((child) => {
    if (child.isMesh && child.material) {
      // Check if material has an allowed name
      const material = Array.isArray(child.material)
        ? child.material[0]
        : child.material

      // Check material name (supports base names and numeric variants)
      const materialName = material.name || ''
      if (isAllowedMaterialName(materialName)) {
        meshes.push(child)
      }
    }
  })

  const intersects = raycasterRef.current.intersectObjects(meshes, false)

  if (intersects.length > 0) {
    return intersects[0].object
  }

  return null
}

/**
 * Perform raycast dari center of screen (untuk touch controls)
 */
export function performCenterRaycast(
  cameraRef,
  selectedCameraRef,
  modelRef,
  sceneRef,
  groundPlaneRef,
  controlsRef,
  THREE
) {
  // Determine active camera
  let activeCamera = cameraRef.current
  if (
    selectedCameraRef.current !== 'default' &&
    modelRef.current?.userData.cameras
  ) {
    const cameraData = modelRef.current.userData.cameras.find(
      (c) => c.name === selectedCameraRef.current
    )
    if (cameraData && cameraData.camera) {
      activeCamera = cameraData.camera
      activeCamera.updateMatrixWorld(true)
    }
  }

  // Center of screen in normalized device coordinates (0, 0)
  const centerMouse = new THREE.Vector2(0, 0)

  // Ensure camera projection matrix is up to date
  activeCamera.updateProjectionMatrix()

  // Create raycaster from center of screen
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(centerMouse, activeCamera)

  // Use a reasonable threshold for intersection detection
  raycaster.params.Points = { threshold: 0.1 }
  raycaster.params.Line = { threshold: 0.1 }

  // Try to intersect with model meshes first
  let intersects = []
  if (modelRef.current) {
    intersects = raycaster.intersectObject(modelRef.current, true)

    // Filter out helpers, ground plane, and non-visible objects
    intersects = intersects.filter((intersect) => {
      const obj = intersect.object
      return (
        obj.isMesh &&
        !obj.isHelper &&
        obj.visible &&
        obj !== groundPlaneRef.current
      )
    })
  }

  // Calculate anchor point based on raycast
  const anchorPoint = new THREE.Vector3()

  if (intersects.length > 0) {
    // If we hit something, use the closest intersection point
    intersects.sort((a, b) => a.distance - b.distance)
    anchorPoint.copy(intersects[0].point)
  } else {
    // If no intersection, project center to a plane at the current target distance
    const distance = activeCamera.position.distanceTo(controlsRef.current.target)
    const direction = new THREE.Vector3()
    direction
      .subVectors(activeCamera.position, controlsRef.current.target)
      .normalize()

    // Create a plane perpendicular to camera direction at target position
    const planeNormal = direction.clone()
    const planeDistance = -controlsRef.current.target.dot(planeNormal)
    const plane = new THREE.Plane(planeNormal, planeDistance)
    const intersection = new THREE.Vector3()

    // Find intersection point on the plane
    if (raycaster.ray.intersectPlane(plane, intersection)) {
      anchorPoint.copy(intersection)
    } else {
      // Fallback: project to plane at fixed distance from camera
      const worldPoint = new THREE.Vector3()
      raycaster.ray.at(distance, worldPoint)
      anchorPoint.copy(worldPoint)
    }
  }

  return anchorPoint
}

